import { filterSocialPosts } from './filters';
import { platformStyle } from './platforms';
import type { SocialFilters } from './filters';
import type { SocialPost } from './queries';

/**
 * Angel's LinkedIn Calendar: the main Calendar, narrowed to one platform.
 *
 * No new data. A post is "on LinkedIn" when LinkedIn is among its platforms,
 * the same test the Platform dropdown already runs, so a post tagged
 * Instagram and LinkedIn shows in both calendars (Kyu's default A, 14 Oct).
 * One post, one record, never a copy.
 *
 * Format is the existing free-text Type field (default C). A fixed list would
 * be a promise the database does not keep.
 */

/** The platform key this tab is fixed to, as platformStyle normalises it. */
export const LINKEDIN_KEY = platformStyle('LinkedIn').key;

/**
 * What a post created on this tab is saved with. Spelled as the label, since
 * the drawer shows the raw list ("LinkedIn", not "linkedin").
 */
export const LINKEDIN_PLATFORMS: string[] = [platformStyle('LinkedIn').label];

/**
 * The posts the tab shows. The shared filters still apply, except platform,
 * which is fixed: a platform chosen on the main Calendar must not empty this
 * tab without a visible control to undo it.
 */
export function linkedinCalendarPosts(
  posts: SocialPost[],
  filters: SocialFilters,
  format: string,
): SocialPost[] {
  return filterSocialPosts(posts, { ...filters, platform: LINKEDIN_KEY }).filter(
    (post) => format === '' || post.type.trim() === format,
  );
}

/** The Format dropdown's options: every Type someone has typed, blanks left out. */
export function formatOptions(posts: SocialPost[]): string[] {
  const seen = new Set<string>();
  for (const post of posts) {
    const type = post.type.trim();
    if (type !== '') {
      seen.add(type);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** "09:30" from the database's "09:30:00"; nothing when no time is set. */
export function pillTime(publishTime: string | null): string | null {
  if (publishTime === null || publishTime.trim() === '') {
    return null;
  }
  return publishTime.slice(0, 5);
}

/**
 * The format dot (Angel, 14 Oct: "even just a color dot" to tell a photo from
 * a reel at a glance).
 *
 * Type is free text, so this reads it forgivingly: case and spacing ignored,
 * a trailing plural s dropped, whole words only ("Context" is not Text). The
 * typed word itself still shows beside the dot; the dot only groups it.
 */
export type FormatKind = 'photo' | 'reel' | 'document' | 'text' | 'poll' | 'article' | 'other';

/** The legend, in the order it reads. Other is last: it is the leftover. */
export const FORMAT_KINDS: { kind: FormatKind; label: string }[] = [
  { kind: 'photo', label: 'Photo' },
  { kind: 'reel', label: 'Reel/Video' },
  { kind: 'document', label: 'Document' },
  { kind: 'text', label: 'Text' },
  { kind: 'poll', label: 'Poll' },
  { kind: 'article', label: 'Article' },
  { kind: 'other', label: 'Other or not set' },
];

/**
 * Checked in this order, first hit wins. A word naming a specific format beats
 * a generic one: "Text + image" is a photo post with words on it, and a "PDF
 * carousel" is a document. An article with a header image is still an
 * article, so article comes before photo. Carousel (a photo unless said
 * otherwise) and text (what every post has) come last.
 */
const FORMAT_WORDS: [FormatKind, string[]][] = [
  ['reel', ['reel', 'video']],
  ['document', ['document', 'doc', 'pdf']],
  ['article', ['article']],
  ['photo', ['photo', 'image', 'picture', 'pic', 'graphic', 'infographic']],
  ['poll', ['poll']],
  ['photo', ['carousel']],
  ['text', ['text']],
];

export function formatKind(type: string): FormatKind {
  const words = new Set(
    type
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((word) => word !== '')
      .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word)),
  );
  for (const [kind, list] of FORMAT_WORDS) {
    if (list.some((word) => words.has(word))) {
      return kind;
    }
  }
  return 'other';
}

export function formatKindLabel(kind: FormatKind): string {
  return FORMAT_KINDS.find((entry) => entry.kind === kind)?.label ?? 'Other or not set';
}
